import {
  compact, filter, find, map,
  includes, sumBy, orderBy, pickBy,
  groupBy, isEmpty,
} from 'lodash';
import { createSelector } from '@reduxjs/toolkit';
import { getShowIdFromPath } from '@client/selectors/helpers/router';
import { getDateTime } from '@sda/utils/date';
import { TicketStatuses, ProfileTypes } from '@sda/enums';
import { DEFAULT_CURRENCY } from '@sda/utils/lang';
import { generatePricingInformation } from '@sda/utils/meta';
import { isPrimitive } from '@sda/utils/type';
import { SHOW_END_HOUR_INTERVAL } from '@sda/constants';
import {
  showSelectors, ticketSelectors, fingerprintSelector, currentShowIdSelector,
  stripeCurrentSKUSelector, showMetadataSelector, productSelectors, actSelectors,
  venueSelectors, profileSelectors,
} from '..';
import { selectItemById, defaultItemSelectorById, selectItemByOptionalId } from '../lib';
import { getSessionUserAdminStatus, getUserId } from '../helpers/login';
import { getSelectedProfileId } from '../helpers/profile';
import { getThreadShowId } from './threads';
import { getCountries } from './categories';
import type { NormalizedPublicProfile } from '@sda/interfaces/show';
import type { DateTimeInput } from '@sda/utils/date/interfaces';
import type { ShowMetadata } from '@sda/interfaces/reducer/shows.reducer';
import type { PublicTicket } from '@sda/interfaces/ticket';

export const DefaultShowMetadata = {
  numTicketsPurchased: 0,
  numTotalGuests: 0,
  numGuestJoined: 0,
  numAssignedCompTickets: 0,
  numClaimedCompTickets: 0,
  productNumTicketsPurchased: {},
};

export const getShowById = showSelectors.selectById;

/**
 * Check to see if the ticket has been reserved by someone other than the current guest
 * @param  {string} reservedUntil
 * @param  {String | Null} hash
 * @param  {string} fingerprint
 * @return {boolean}
 */
const ticketIsAvailable = ({ reservedUntil, hash }: Pick<PublicTicket, 'reservedUntil' | 'hash'>, fingerprint: string): boolean => {
  const reservationIsExpired = getDateTime(reservedUntil) < getDateTime();
  const reservedByCurrentGuest = (hash === fingerprint);

  return (reservationIsExpired || reservedByCurrentGuest);
};

export const getShowTickets = createSelector(
  ticketSelectors.selectEntities,
  stripeCurrentSKUSelector,
  fingerprintSelector,
  (items, currentSKU, fingerprint) => {
    if (!currentSKU || !currentSKU.price) {
      return [];
    }

    if (typeof currentSKU.price === 'number') {
      return filter(
        items,
        (ticket) => (
          ticket.showId === currentSKU.showId
          && ticketIsAvailable(ticket, fingerprint)
          && ticket.status === TicketStatuses.AVAILABLE),
      );
    }

    const skuProductIds = map(currentSKU.price, (price, productId) => productId);

    return filter(
      items,
      (ticket) => (
        skuProductIds.length > 0
        && includes(skuProductIds, ticket.stripeSKU)
        && ticketIsAvailable(ticket, fingerprint)
        && ticket.status === TicketStatuses.AVAILABLE),
    );
  },
);

/**
 * Retrieves a list of all tickets purchased for a given show
 */
export const getPurchasedTicketsForShow = createSelector(
  ticketSelectors.selectEntities,
  selectItemById,
  (items, showId) => pickBy(
    items,
    (ticket) => (ticket.showId === showId && ticket.status === TicketStatuses.PURCHASED),
  ),
);

/**
 * Retrieves a list of tickets owned or purchased for a given show by the current user
 * This includes tickets that were purchased by the current user but transferred
 */
export const getUserTicketsForShow = createSelector(
  ticketSelectors.selectEntities,
  selectItemById,
  getUserId,
  (items, showId, userId) => userId && filter(
    items,
    (ticket) => (
      ticket.showId === showId
      && (ticket.status === TicketStatuses.PASS
        || ticket.status === TicketStatuses.PURCHASED
        || ticket.status === TicketStatuses.COMPED)
      && ticket.collaborators
      && ticket.collaborators.indexOf(userId) !== -1
    ),
  ),
);

export const getCompedTicketsForShow = createSelector(
  ticketSelectors.selectEntities,
  selectItemById,
  (items, showId) => pickBy(
    items,
    (ticket) => (ticket.showId === showId && (ticket.status === TicketStatuses.COMPED || ticket.isCompedTicket)),
  ),
);

/**
 * Fetches the metadata for a show by ID
 * @param  {string} showId
 */
export const getMetadataByShowId = createSelector(
  showMetadataSelector,
  selectItemById,
  (showMetadata, showId): Partial<ShowMetadata> => {
    if (!showMetadata || !showId || !showMetadata[showId]) {
      return DefaultShowMetadata;
    }

    return { ...DefaultShowMetadata, ...showMetadata[showId] };
  },
);

export const getCurrentShowId = createSelector(
  selectItemByOptionalId,
  currentShowIdSelector,
  getThreadShowId,
  getShowIdFromPath,
  (providedId, currentShowId, threadShowId, matchShowId) => {
    if (providedId && isPrimitive(providedId)) {
      return providedId;
    }

    return currentShowId || threadShowId || matchShowId;
  },
);

export const getCurrentShow = createSelector(
  showSelectors.selectEntities,
  getCurrentShowId,
  (items, showId) => {
    const show = defaultItemSelectorById(items, showId);

    return show;
  },
);

/**
 * Retrieves all profiles for the current show
 */
export const getCurrentShowProfiles = createSelector(
  getCurrentShow,
  actSelectors.selectEntities,
  venueSelectors.selectEntities,
  (show, acts, venues) => {
    const profiles = compact(map(show?.publicProfiles, (profile) => {
      if (profile.profileType === ProfileTypes.ACT) {
        return acts[profile.profileId] as any;
      }

      if (profile.profileType === ProfileTypes.VENUE) {
        return venues[profile.profileId] as any;
      }

      return null;
    })) as NormalizedPublicProfile[];

    return profiles;
  },
);

/**
 * Retrieves all profiles for the current show, including payees
 */
export const getCurrentShowPayeeProfiles = createSelector(
  getCurrentShow,
  actSelectors.selectEntities,
  venueSelectors.selectEntities,
  profileSelectors.selectEntities,
  (show, acts, venues, pendingProfiles) => {
    if (isEmpty(show)) {
      return [];
    }

    const profiles = compact(map(show.publicProfiles, (profile) => {
      if (profile.profileType === ProfileTypes.ACT) {
        return acts[profile.profileId] as any;
      }

      if (profile.profileType === ProfileTypes.VENUE) {
        return venues[profile.profileId] as any;
      }

      if (profile.profileType === ProfileTypes.PENDING) {
        const privateProfile = pendingProfiles[profile.profileId];
        const privateProfileData = show.privateProfiles?.find((publicProfile) => publicProfile.profileId === profile.profileId);

        return {
          ...profile,
          ...privateProfile || {},
          ...privateProfileData || {},
          id: profile.profileId,
        } as any;
      }

      return null;
    })) as NormalizedPublicProfile[];

    return profiles;
  },
);

export const getCurrentShowOwnerProfile = createSelector(
  getCurrentShow,
  getCurrentShowProfiles,
  (show, profiles) => profiles.find((profile) => profile.id === show.ownerProfileId),
);

export const getCurrentShowProfilesLoaded = createSelector(
  getCurrentShow,
  getCurrentShowPayeeProfiles,
  (show, profiles) => show.profileIds.length === profiles.length,
);

export const getCurrentShowOtherProfiles = createSelector(
  getCurrentShowProfiles,
  getSelectedProfileId,
  (profiles, selectedProfileId) => profiles.filter((profile) => profile.id !== selectedProfileId),
);

export const getCurrentShowOtherPayees = createSelector(
  getCurrentShowPayeeProfiles,
  getSelectedProfileId,
  (profiles, selectedProfileId) => profiles.filter((profile) => profile.id !== selectedProfileId),
);

export const getCurrentShowCurrency = createSelector(
  getCurrentShow,
  getCountries,
  (show, countries) => {
    if (isEmpty(show)) {
      return DEFAULT_CURRENCY;
    }

    if (show.currencyOverride) {
      return show.currencyOverride;
    }

    if (!show.country) {
      return DEFAULT_CURRENCY;
    }

    const country = find(countries, (obj) => obj.metadata.iso2.toUpperCase() === show.country.toUpperCase());

    return country?.metadata?.defaultCurrency || DEFAULT_CURRENCY;
  },
);

export const getCurrentShowIsLocked = createSelector(
  getCurrentShow,
  (show) => !show?.isIncomplete || false,
);

export const getApplyTzForShow = createSelector(
  getCurrentShow,
  (show) => (dateStamp: DateTimeInput) => {
    if (show?.isOnlineOnly) {
      return getDateTime(dateStamp);
    }

    if (show?.timezoneOverride) {
      return getDateTime(dateStamp).setZone(show.timezoneOverride);
    }

    return getDateTime(dateStamp);
  },
);

export const getProductsForShow = createSelector(
  productSelectors.selectAll,
  getCurrentShowId,
  (items, showIdToFilter) => filter(
    items,
    (item) => (
      item.showId === showIdToFilter
          && item.isActive
    ),
  ),
);

/**
 * Retrieves the lowest price possible for the current show
 */
export const getShowPricingInformation = createSelector(
  getProductsForShow,
  generatePricingInformation,
);

/**
 * Returns the total capacity of all products for the current show
 */
export const getProductsForShowTotalCapacity = createSelector(
  getProductsForShow,
  (products) => sumBy(products, (product) => (product.capacity > 0 ? product.capacity : 0)),
);

/**
 * Returns the total number of all tickets purchased across all products for the current show
 */
export const getProductsForShowNumTicketsPurchased = createSelector(
  getProductsForShow,
  (products) => sumBy(products, (product) => product.numTicketsPurchased || 0),
);

/**
 * returns a single product by id
 */
export const getProductById = productSelectors.selectById;

/**
 * Retrieves a list of tickets owned for a given show by the current user
 */
export const getUserOwnedTicketsForCurrentShow = createSelector(
  ticketSelectors.selectEntities,
  getCurrentShow,
  getUserId,
  (items, show, userId) => orderBy(
    filter(
      items,
      (ticket) => (
        ticket.showId === show?.id
        && (ticket.status === TicketStatuses.PURCHASED
          || ticket.status === TicketStatuses.COMPED
          || ticket.status === TicketStatuses.PASS)
        && ticket.owner === userId
        && !ticket.pendingRedemption
      ),
    ),
    (item) => getDateTime(item.purchaseTime),
    ['desc', 'asc'],
  ),
);

/**
 * Retrieves the first purchased ticket for the current show defaulting to an empty obj.
 * Also prioritizes registered tickets
 */
export const getUserOwnedTicketForCurrentShow = createSelector(
  getUserOwnedTicketsForCurrentShow,
  (tickets) => {
    const registeredTicket = find(tickets, (ticket) => ticket.registeredAt || ticket.replayJoinedAt || ticket.streamJoinUrl || ticket.replayJoinUrl);

    if (registeredTicket) {
      return registeredTicket;
    }

    return tickets && (tickets.length > 0 ? tickets[0] : {});
  },
);

/**
 * Checks whether the user owns both profiles in the given show
 */
export const getCurrentShowOwnsAllProfiles = createSelector(
  getCurrentShowPayeeProfiles,
  getUserId,
  (profiles, userId) => filter(profiles, (profile) => profile.owner === userId).length === profiles.length,
);

export const getCurrentShowIsTwoPartyShow = createSelector(
  getCurrentShow,
  (show) => {
    if (isEmpty(show)) {
      return false;
    }

    return show.publicProfiles.length > 1;
  },
);

export const getCurrentShowIsConnectedShow = createSelector(
  getCurrentShow,
  (show) => {
    if (!show) {
      return false;
    }

    return show.publicProfiles.length > 1 && !show.publicProfiles.find((profile) => profile.profileType === ProfileTypes.PENDING);
  },
);

export const getCurrentShowIsShowOwner = createSelector(
  getCurrentShow,
  getSelectedProfileId,
  getSessionUserAdminStatus,
  (show, selectedProfileId, isAdmin) => {
    if (isEmpty(show)) {
      return false;
    }

    if (isAdmin) {
      return true;
    }

    if (show.ownerProfileId === selectedProfileId) {
      return true;
    }

    return false;
  },
);

export const getCurrentShowIsCollaboratorOrOwner = createSelector(
  getCurrentShow,
  getUserId,
  getCurrentShowIsShowOwner,
  (show, userId, isOwner) => {
    if (isOwner) {
      return true;
    }

    return show?.collaborators.includes(userId);
  },
);

export const getCurrentShowIsModerator = createSelector(
  getCurrentShow,
  getSelectedProfileId,
  getUserId,
  getCurrentShowIsCollaboratorOrOwner,
  getSessionUserAdminStatus,
  (show, selectedProfileId, userId, isOwnerOrCollaborator, isAdmin) => {
    if (isOwnerOrCollaborator || isAdmin) {
      return false;
    }

    if (!show || isEmpty(show.moderators) || isEmpty(show.profileIds)) {
      return false;
    }

    return show.moderators.includes(userId) && !show.profileIds.includes(selectedProfileId);
  },
);

/**
 * Retrieve all shows for the current profile that the current user has access to
 */
export const getShowsForProfile = createSelector(
  showSelectors.selectAll,
  getSelectedProfileId,
  getUserId,
  (shows, selectedProfileId, userId) => {
    const filteredShows = shows.filter(
      (show) => show.profileIds.includes(selectedProfileId)
      && show.isHyperwalletShow,
    );

    const moderatedShows = shows.filter(
      (show) => {
        if (!show.isHyperwalletShow) {
          return false;
        }

        if (selectedProfileId && show.profileIds.includes(selectedProfileId)) {
          return false;
        }

        return show.moderators?.includes(userId) && !show.collaborators?.includes(userId);
      },
    );

    const groupedShows = groupBy(filteredShows, (show) => getDateTime(show.startTime).plus({ hours: SHOW_END_HOUR_INTERVAL }) >= getDateTime());

    return {
      shows: [...(groupedShows.true || []), ...moderatedShows],
      expiredShows: groupedShows.false || [],
    };
  },
);

/**
 * Fetches the number of tickets that are currently being checked out by guests
 */
export const getUnavailableTicketCountForProduct = createSelector(
  getProductById,
  ticketSelectors.selectEntities,
  (product, tickets) => {
    if (!product) {
      return 0;
    }

    const ticketsForProduct = Object.values(tickets || {}).filter((ticket) => ticket.stripeSKU === product.id
      && (ticket.status === TicketStatuses.AVAILABLE || ticket.status === TicketStatuses.DEPRECATED)
      && getDateTime(ticket.reservedUntil) > getDateTime()
      && ticket.hash);

    return (product.numTicketsPurchased || 0) + ticketsForProduct.length;
  },
);
